/**
 * Speaker profiles -- group 10.
 *
 * Seven Italian designers plus one German and one Dutch house, chosen because
 * they voice by very different means: an open baffle with a compression driver,
 * a low-Q sealed two-way, three reflex designs with different tuning ratios and
 * crossover alignments, a design that offsets its tweeter physically instead of
 * electrically, a partly active floorstander, and a ribbon-and-ceramic system
 * in a densified-wood cabinet.
 *
 * The kit derives the anechoic baseline from the published bass alignment and
 * high-frequency limit; the authored `responseTilt` is the voicing, and it is
 * the part of each entry that carries the design's actual tonal signature.
 * Impedance is modelled from the alignment so low-feedback amplifiers interact
 * with it correctly rather than seeing a flat 8 ohm resistor.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_10: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Diesis Audio',
    model: 'Ludos',
    price: usd(28000, 'APPROXIMATE', 2020, 'Price per pair; recalled approximately and not verified against a current dealer list.'),
    year: 2015,
    country: 'Italy',
    speakerType: 'open-baffle',
    enclosure: 'open-baffle',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 95,
    nominalOhms: 8,
    minOhms: 6.0,
    bass: [42, 30],
    hfExtension: 22000,
    crossovers: [{ freqHz: 900, slopeDbOct: 6, alignment: 'first-order' }],
    responseTilt: [[20, 0.4], [60, -0.6], [150, 0.8], [400, -0.5], [1000, 0.3], [2500, 1.1], [5000, -0.9], [10000, 0.6], [20000, -1.8]],
    resonances: [[190, -32, 8], [640, -36, 11]],
    coverageAngleDeg: 90,
    baffleStepHz: 170,
    recommendedWatts: [8, 150],
    dimensionsMm: [1500, 700, 400],
    massKg: 58,
    thdPercent: 0.5,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#b06a3a',
    tags: ['dipole', 'compression-driver', 'first-order-crossover', 'high-sensitivity'],
    notes:
      'No enclosure at all: the drivers work on a folded open panel, so the front and rear radiation cancel below the path-length frequency and the low-frequency rolloff is a shallow dipole slope rather than a box alignment. That gives a figure-of-eight radiation pattern which puts a null at the side walls, so this design excites far less lateral room energy than any box speaker here while depending heavily on the distance to the wall behind it. A first-order crossover at 900 Hz means the compression driver is still contributing an octave below crossover and the woofers an octave above, which is why the harmonic structure is modelled with the horn distribution -- distortion rises with SPL from air nonlinearity in the throat rather than from voice-coil excursion.',
    provenanceNote:
      'Open-baffle construction, the compression-driver top end and the high sensitivity are from Diesis Audio published material. Bass extension, impedance, crossover point and slope, dimensions and mass are estimates for this model; Diesis does not publish a full measured specification. The dipole rolloff and radiation pattern are modelled from the open-baffle loading.',
  }),

  makeSpeaker({
    mfr: 'Franco Serblin',
    model: 'Accordo',
    price: usd(11000, 'APPROXIMATE', 2018, 'Price per pair without the dedicated stands; recalled approximately.'),
    year: 2012,
    country: 'Italy',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 87,
    nominalOhms: 8,
    minOhms: 5.8,
    bass: [48, 40],
    hfExtension: 26000,
    portTuningHz: 46,
    crossovers: [{ freqHz: 2200, slopeDbOct: 12, alignment: 'butterworth' }],
    responseTilt: [[20, 0], [60, 0.3], [150, 0.5], [400, -0.3], [1200, -0.6], [3000, -1.1], [6000, 0.4], [10000, -0.2], [20000, -1.4]],
    resonances: [[240, -30, 8], [560, -34, 11]],
    coverageAngleDeg: 130,
    recommendedWatts: [20, 150],
    dimensionsMm: [370, 210, 290],
    massKg: 9,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8c6f52',
    tags: ['two-way', 'soft-dome', 'presence-dip', 'small-baffle'],
    notes:
      'A small two-way in a narrow curved cabinet whose voicing is a deliberate shallow depression through the upper midrange and lower treble, in the BBC tradition, with the top octave rolled rather than lifted. The narrow baffle puts the baffle-step transition high, around 550 Hz, so a great deal of the perceived tonal balance depends on how far the speaker sits from the front wall -- the model applies the step rather than pretending the response is anechoically flat. Its low-frequency limit near 48 Hz with a moderate reflex tuning means excursion-limited distortion rises steeply below about 65 Hz, so the design trades bass reach for a small radiating area and low diffraction.',
    provenanceNote:
      'Two-way configuration, cabinet dimensions and approximate sensitivity are from Franco Serblin published material. The reflex tuning, minimum impedance, crossover frequency and slope, and distortion figure are estimates; no measured anechoic data is used here. The voicing tilt is authored from listening-oriented descriptions and is explicitly a modelled shape.',
  }),

  makeSpeaker({
    mfr: 'Rosso Fiorentino',
    model: 'Certaldo',
    price: usd(11000, 'APPROXIMATE', 2019, 'Price per pair; recalled approximately and not verified.'),
    year: 2016,
    country: 'Italy',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 4,
    minOhms: 3.1,
    bass: [34, 27],
    hfExtension: 25000,
    portTuningHz: 32,
    crossovers: [
      { freqHz: 300, slopeDbOct: 12, alignment: 'butterworth' },
      { freqHz: 2500, slopeDbOct: 18, alignment: 'butterworth' },
    ],
    responseTilt: [[20, 0], [50, 0.6], [120, -0.4], [350, 0.2], [900, -0.2], [2200, 0.5], [5000, 0.8], [11000, 0.4], [20000, -0.7]],
    resonances: [[155, -34, 12], [430, -38, 16]],
    coverageAngleDeg: 120,
    recommendedWatts: [40, 250],
    dimensionsMm: [1080, 240, 340],
    massKg: 38,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#a83b3b',
    tags: ['three-way', 'constrained-layer-cabinet', 'rising-treble', 'four-ohm-load'],
    notes:
      'A conventional three-way reflex, but voiced with a gently rising presence and lower treble rather than the depression used by the Serblin entry -- roughly 0.8 dB up at 5 kHz against a flat midrange, which is a small number that changes the perceived balance considerably. Nominal 4 ohms with a 3.1 ohm minimum near the lower crossover means the current demand is roughly double an 8 ohm design at the same level, so this profile is the one that separates amplifiers with real output-stage current capability from those without. The constrained-layer cabinet is modelled as two relatively high-Q but very low-level wall resonances, which is the signature of a heavily damped rigid box rather than a thin-wall design.',
    provenanceNote:
      'Three-way configuration, nominal impedance, sensitivity and cabinet construction are from Rosso Fiorentino published material. Bass extension, port tuning, crossover frequencies and slopes, minimum impedance and distortion are estimated. The cabinet resonance model follows from the stated constrained-layer construction and is not a measurement.',
  }),

  makeSpeaker({
    mfr: 'Chario',
    model: 'Academy Sovran',
    price: usd(13000, 'APPROXIMATE', 2016, 'Price per pair; recalled approximately for the Academy line flagship.'),
    year: 2011,
    country: 'Italy',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 90,
    nominalOhms: 6,
    minOhms: 4.4,
    bass: [30, 25],
    hfExtension: 23000,
    portTuningHz: 29,
    crossovers: [
      { freqHz: 220, slopeDbOct: 12, alignment: 'bessel' },
      { freqHz: 1900, slopeDbOct: 6, alignment: 'first-order', offsetMm: 90 },
    ],
    responseTilt: [[20, 0], [55, 0.4], [130, 0.9], [300, 0.3], [800, -0.4], [2000, -0.9], [4500, -0.4], [9000, 0.5], [20000, -1.1]],
    resonances: [[138, -30, 9], [395, -35, 13]],
    coverageAngleDeg: 125,
    recommendedWatts: [30, 200],
    dimensionsMm: [1120, 300, 420],
    massKg: 45,
    thdPercent: 0.32,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#7a8f5c',
    tags: ['tweeter-below-midrange', 'shallow-slopes', 'physical-time-offset', 'lower-mid-lift'],
    notes:
      'The defining choice is mounting the tweeter below the midrange with a deliberate front-to-back offset so that the acoustic centres line up at a seated listening height rather than on the tweeter axis; the crossover section here carries that as a 90 mm physical offset instead of an electrical delay. A first-order acoustic slope at the top crossover keeps phase continuous through the transition but leaves both drivers working well outside their passbands, so the vertical lobing pattern is unusually sensitive to listening height. Voicing adds close to a decibel of lower-midrange lift around 130 Hz and pulls the presence region back by a similar amount, which is a different mechanism from the Rosso Fiorentino entry even though both are three-way reflex floorstanders.',
    provenanceNote:
      'The inverted driver arrangement, gentle crossover slopes and three-way configuration are from Chario published material. Crossover frequencies, driver offset, port tuning, impedance minimum, extension and distortion are estimates; Chario publishes tonal-design intent more fully than it publishes measured data. The lobing consequence is a modelled result of the stated geometry.',
  }),

  makeSpeaker({
    mfr: 'Opera Loudspeakers',
    model: 'Grand Callas',
    price: usd(14000, 'APPROXIMATE', 2017, 'Price per pair; recalled approximately and not verified against a current dealer list.'),
    year: 2013,
    country: 'Italy',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 91,
    nominalOhms: 6,
    minOhms: 4.0,
    bass: [36, 31],
    hfExtension: 24000,
    portTuningHz: 37,
    crossovers: [[350, 12], [2800, 24]],
    responseTilt: [[20, 0], [45, 0.8], [110, 0.5], [280, -0.6], [700, 0.3], [1800, 0.2], [4000, -0.7], [8000, 0.6], [20000, -1.6]],
    resonances: [[125, -26, 6], [310, -29, 8], [720, -33, 10]],
    coverageAngleDeg: 120,
    recommendedWatts: [20, 200],
    dimensionsMm: [1150, 260, 380],
    massKg: 42,
    thdPercent: 0.38,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#9b7b4f',
    tags: ['solid-wood-cabinet', 'high-tuning-reflex', 'valve-friendly', 'mixed-slopes'],
    notes:
      'A solid-timber cabinet rather than a braced composite panel, which is modelled as three cabinet resonances of lower Q and higher level than the rigid boxes in this group -- an audible but diffuse contribution in the lower midrange rather than a narrow spike. The reflex is tuned high relative to its f3, which produces a fourth-order rolloff that is steep below tuning but keeps port air velocity low and makes the design tolerant of near-wall placement. Ninety-one decibels of sensitivity with a 4 ohm minimum only at the lower crossover makes this the entry in the group most usable on a low-power valve amplifier, which is exactly what its house partnership implies.',
    provenanceNote:
      'Solid-wood cabinet construction, three-way configuration and approximate sensitivity are from Opera published material. Bass extension, port tuning, crossover frequencies and slopes, minimum impedance, mass and distortion are estimates. The resonance model follows from the stated solid-timber construction and is explicitly modelled.',
  }),

  makeSpeaker({
    mfr: 'Diapason',
    model: 'Adamantes',
    price: usd(6500, 'APPROXIMATE', 2018, 'Price per pair for the current Adamantes revision; recalled approximately.'),
    year: 2010,
    country: 'Italy',
    speakerType: 'standmount',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 6.5,
    bass: [55, 45],
    hfExtension: 25000,
    crossovers: [{ freqHz: 2500, slopeDbOct: 12, alignment: 'linkwitz-riley', offsetMm: 25 }],
    responseTilt: [[20, 0], [70, -0.4], [160, 0.6], [420, 0.4], [1100, -0.5], [2800, 0.7], [6500, -0.6], [12000, 0.3], [20000, -0.9]],
    resonances: [[330, -40, 18], [810, -44, 24]],
    coverageAngleDeg: 130,
    recommendedWatts: [25, 120],
    dimensionsMm: [360, 200, 280],
    massKg: 11,
    thdPercent: 0.4,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#6d5f7d',
    tags: ['sealed', 'solid-hardwood', 'non-parallel-walls', 'offset-tweeter'],
    notes:
      'A sealed two-way in a solid hardwood cabinet with non-parallel walls and a laterally offset tweeter, which is the opposite alignment choice from every reflex design in this group. Sealed loading gives a second-order rolloff, so although its 55 Hz f3 is the highest here the response below that point falls at half the rate of the reflex entries and integrates with room gain instead of fighting it; group delay in the bass is correspondingly the lowest in the group. The non-parallel walls push internal standing waves up in frequency and the dense hardwood raises their Q while lowering their level, which the model carries as two narrow, very low-level resonances above 300 Hz rather than the broad low-frequency contributions of the Opera entry.',
    provenanceNote:
      'Sealed loading, solid hardwood cabinet, non-parallel walls and the offset tweeter are from Diapason published material. Sensitivity, impedance minimum, bass extension, crossover frequency and slope, mass and distortion are estimates for this revision. Cabinet resonance frequencies and Q are modelled from the stated construction, not measured.',
  }),

  makeSpeaker({
    mfr: 'Aequo Audio',
    model: 'Ensis',
    price: usd(40000, 'APPROXIMATE', 2019, 'Price per pair; recalled approximately and not verified against a current dealer list.'),
    year: 2018,
    country: 'Netherlands',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['amt', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    active: true,
    sensitivityDb: 90,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [26, 21],
    hfExtension: 40000,
    crossovers: [
      { freqHz: 120, slopeDbOct: 24, alignment: 'digital-lr' },
      { freqHz: 2400, slopeDbOct: 12, alignment: 'linkwitz-riley' },
    ],
    responseTilt: [[20, 0.5], [45, 0.9], [110, -0.5], [300, -0.3], [900, 0.2], [2500, -0.6], [6000, 0.7], [12000, 1.0], [20000, 0.4]],
    resonances: [[210, -44, 20], [520, -48, 26]],
    coverageAngleDeg: 100,
    recommendedWatts: [30, 300],
    dimensionsMm: [1150, 290, 420],
    massKg: 65,
    thdPercent: 0.2,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#3f8f8a',
    tags: ['active-bass', 'amt-tweeter', 'digital-low-crossover', 'hybrid-drive'],
    notes:
      'Only the bass section is powered: an on-board amplifier and digital filter drive a sealed low-frequency system up to about 120 Hz, while the midrange and AMT tweeter remain passive and are driven by the user amplifier. That split is the reason this profile keeps a real impedance curve with a 3.2 ohm minimum instead of behaving like a line-level input, and it also means the bass rolloff and the bass distortion are decoupled from whatever amplifier is in front of it. The AMT diaphragm folds its membrane so the air velocity exceeds the diaphragm velocity, which sustains output past 30 kHz with lower moving mass than a dome, and the voicing exploits that with a mild top-octave lift rather than the rolloff used by the soft-dome designs here.',
    provenanceNote:
      'The powered bass section with digital low crossover, the AMT tweeter and the sealed loading are from Aequo Audio published material. Bass extension, sensitivity, impedance minimum, crossover frequencies and slopes, mass and distortion are estimates. The consequences of the hybrid active/passive split described above are modelled, not measured.',
  }),

  makeSpeaker({
    mfr: 'Kaiser Acoustics',
    model: 'Kawero! Classic',
    price: usd(60000, 'APPROXIMATE', 2018, 'Price per pair; recalled approximately and varies with finish, so treat as indicative.'),
    year: 2011,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['ribbon', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.4,
    bass: [28, 23],
    hfExtension: 45000,
    portTuningHz: 27,
    crossovers: [
      { freqHz: 250, slopeDbOct: 12, alignment: 'bessel' },
      { freqHz: 2800, slopeDbOct: 12, alignment: 'bessel' },
    ],
    responseTilt: [[20, 0], [40, 0.3], [100, -0.3], [250, -0.7], [800, 0.1], [2000, 0.4], [5000, -0.5], [10000, 0.9], [20000, 1.2]],
    resonances: [[420, -48, 26], [1050, -52, 32]],
    coverageAngleDeg: 100,
    recommendedWatts: [40, 300],
    dimensionsMm: [1170, 300, 450],
    massKg: 90,
    thdPercent: 0.18,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#5b6c7a',
    tags: ['ribbon-tweeter', 'ceramic-midrange', 'densified-wood-cabinet', 'bessel-alignment'],
    notes:
      'A true ribbon tweeter on a ceramic midrange in a cabinet made from resin-densified laminated wood, a material with high internal loss as well as high stiffness. The model reflects that with cabinet resonances at very low level and high Q -- pushed above 400 Hz and roughly 12 dB below the level of the solid-timber Opera entry -- so the audible cabinet contribution is close to negligible. Bessel crossover alignments trade stopband rejection for the flattest group delay through the transition, which combined with a ribbon that is still radiating usefully past 40 kHz is what the top-octave lift in the voicing is exploiting; the tradeoff is a 4 ohm nominal load with a 3.4 ohm minimum and a driver overlap region that demands a well-behaved amplifier.',
    provenanceNote:
      'Ribbon tweeter, ceramic midrange, densified-wood cabinet and the three-way configuration are from Kaiser Acoustics published material. Sensitivity, impedance, bass extension, port tuning, crossover frequencies and alignment, mass and distortion are estimates for this model; Kaiser does not publish a full measured specification. The cabinet resonance model follows from the stated material and is not a measurement.',
  }),
];
